import React, { useState } from 'react';
import { SearchIcon } from 'lucide-react';
// Define search data structure
interface SearchItem {
  title: string;
  description: string;
  url: string;
  section?: string;
}
interface SearchBarProps {
  setSearchResults: (results: SearchItem[]) => void;
  setShowSearchResults: (show: boolean) => void;
}
const SearchBar: React.FC<SearchBarProps> = ({
  setSearchResults,
  setShowSearchResults
}) => {
  const [query, setQuery] = useState('');
  // Search data covering all sections of the guidelines
  const searchData: SearchItem[] = [
  // NCCN Options
  {
    title: 'Targeted Therapies',
    description: 'Biomarkers and therapies in NCCN Guidelines for NSCLC',
    url: '/nccn-options#targeted-therapies',
    section: 'NCCN Options'
  }, {
    title: 'Immunotherapies',
    description: 'Immunotherapy biomarkers and therapies for NSCLC',
    url: '/nccn-options#immunotherapies',
    section: 'NCCN Options'
  }, {
    title: 'Cellular Therapies',
    description: 'Information about cellular therapies for NSCLC',
    url: '/nccn-options#cellular-therapies',
    section: 'NCCN Options'
  }, {
    title: 'Antibodies and ADCs',
    description: 'Antibodies and ADCs in NCCN Guidelines',
    url: '/nccn-options#antibodies-adcs',
    section: 'NCCN Options'
  }, {
    title: 'Chemotherapies',
    description: 'Standard chemotherapy regimens for NSCLC',
    url: '/nccn-options#chemotherapies',
    section: 'NCCN Options'
  },
  // Clinical Trials
  {
    title: 'Clinical Trials Overview',
    description: 'Overview of clinical trials for NSCLC',
    url: '/clinical-trials#overview',
    section: 'Clinical Trials'
  }, {
    title: 'Featured Clinical Trials',
    description: 'Selected trials for late-stage NSCLC',
    url: '/clinical-trials#featured-trials',
    section: 'Clinical Trials'
  }, {
    title: 'Finding Clinical Trials',
    description: 'How to find relevant trials for your condition',
    url: '/clinical-trials#find-trials',
    section: 'Clinical Trials'
  },
  // Off-guideline Drugs
  {
    title: 'Drug Sensitivity Tests',
    description: 'Available DST platforms for NSCLC',
    url: '/off-guideline-drugs#drug-sensitivity',
    section: 'Off-guideline Drugs'
  }, {
    title: 'Off-Guideline Drug Examples',
    description: 'Examples of off-guideline drugs that may show sensitivity',
    url: '/off-guideline-drugs#off-guideline-examples',
    section: 'Off-guideline Drugs'
  }, {
    title: 'Cancer Commons Study',
    description: 'Information about the Cancer Commons Off-Guideline Drug Study',
    url: '/off-guideline-drugs#cancer-commons',
    section: 'Off-guideline Drugs'
  },
  // Whole Health
  {
    title: 'Integrative Oncology Centers',
    description: 'Centers and services for integrative oncology',
    url: '/whole-health#centers-services',
    section: 'Whole Health'
  }, {
    title: 'Nutrition for Cancer Patients',
    description: 'Nutrition recommendations for NSCLC patients',
    url: '/whole-health#nutrition',
    section: 'Whole Health'
  }, {
    title: 'Exercise Guidelines',
    description: 'Exercise recommendations for cancer treatment',
    url: '/whole-health#exercise',
    section: 'Whole Health'
  }, {
    title: 'Stress Reduction',
    description: 'Stress reduction techniques for cancer patients',
    url: '/whole-health#stress-reduction',
    section: 'Whole Health'
  }, {
    title: 'Emotional Wellbeing',
    description: 'Support for emotional wellbeing during cancer treatment',
    url: '/whole-health#emotional-wellbeing',
    section: 'Whole Health'
  },
  // Innovation
  {
    title: 'Recent FDA Approvals',
    description: 'Recent FDA approvals for NSCLC (2024-2025)',
    url: '/innovation#recent-approvals',
    section: 'Innovation'
  }, {
    title: 'Drugs in Development',
    description: 'Investigational drugs for NSCLC',
    url: '/innovation#drugs-in-development',
    section: 'Innovation'
  }, {
    title: 'Research Projects',
    description: 'Ongoing research at academic medical centers',
    url: '/innovation#research-projects',
    section: 'Innovation'
  },
  // End of Life Care
  {
    title: 'Hospice Care',
    description: 'Information about hospice care options',
    url: '/end-of-life-care#hospice',
    section: 'End of Life Care'
  }, {
    title: 'Medical Aid in Dying',
    description: 'Information about Medical Aid in Dying options',
    url: '/end-of-life-care#medical-aid',
    section: 'End of Life Care'
  }];
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim() === '') {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    // Filter search data based on query
    const results = searchData.filter(item => item.title.toLowerCase().includes(query.toLowerCase()) || item.description.toLowerCase().includes(query.toLowerCase()) || item.section && item.section.toLowerCase().includes(query.toLowerCase()));
    setSearchResults(results);
    setShowSearchResults(true);
  };
  return <form onSubmit={handleSearch} className="relative" data-id="element-85">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-86">
        <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-87" />
      </div>
      <input type="text" value={query} onChange={e => setQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search guidelines" data-id="element-88" />
      <button type="submit" className="hidden" data-id="element-89">
        Search
      </button>
    </form>;
};
export default SearchBar;